import type { DirectMessage, Friend } from '@runway/shared';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { DM_MAX_LENGTH, useHub } from '../../state/hub';
import { useSession } from '../../state/session';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';
import { toast } from '../../ui/Toast';
import { cx } from '../../ui/cx';
import { focusableIn, trapTabKey } from '../../ui/focusTrap';
import { UserAvatar } from '../friends/UserAvatar';
import './chat.css';

const EMPTY_MESSAGES: DirectMessage[] = [];
const QUICK_EMOJI = ['✨', '😍', '🔥', '👑', '💅', '😂', '🙌', '💖'];
const EMOJI_ONLY = /^(?:\p{Extended_Pictographic}️?(?:‍\p{Extended_Pictographic}️?)*\s*){1,3}$/u;

/**
 * Slide-over DM drawer usable from anywhere (state lives in useHub so the shell,
 * the friends page and the show lobby can all open it). Friend list on the left,
 * conversation on the right; on phones it is a two-step list → thread.
 */
export function ChatDrawer() {
  const open = useHub((s) => s.chatOpen);
  const close = useHub((s) => s.closeChat);
  const friendId = useHub((s) => s.chatFriendId);
  const friends = useHub((s) => s.friends);
  const setActive = useHub((s) => s.openChat);
  const active = friends.find((f) => f.id === friendId) ?? null;
  const drawerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const drawer = drawerRef.current;
    // Move focus into the drawer unless a child (e.g. the composer) already took it.
    if (drawer && !drawer.contains(document.activeElement)) (focusableIn(drawer)[0] ?? drawer).focus();
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (drawer) trapTabKey(drawer, e);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      previous?.focus?.();
    };
  }, [open, close]);

  if (!open) return null;

  return createPortal(
    <>
      <div className="chat-backdrop" onMouseDown={close} aria-hidden="true" />
      <aside ref={drawerRef} className={cx('chat-drawer', active && 'has-active')} role="dialog" aria-modal="true" aria-label="Messages" tabIndex={-1}>
        <header className="chat-head">
          {active ? (
            <button type="button" className="nav-icon-btn chat-back" aria-label="Back to conversations" onClick={() => setActive(null)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M15 5l-7 7 7 7" />
              </svg>
            </button>
          ) : null}
          {active ? <HeadAvatar friend={active} /> : null}
          <h2>
            {active ? active.displayName : 'Messages'}
            {active ? <ActiveSubtitle friend={active} /> : null}
          </h2>
          <button type="button" className="nav-icon-btn" aria-label="Close messages" onClick={close}>
            ✕
          </button>
        </header>
        <FriendList activeId={friendId} onPick={(id) => setActive(id)} />
        <section className="chat-pane">{active ? <Thread friend={active} /> : <NoThread hasFriends={friends.length > 0} />}</section>
      </aside>
    </>,
    document.body,
  );
}

function HeadAvatar({ friend }: { friend: Friend }) {
  const online = useHub((s) => s.online.has(friend.id));
  return <UserAvatar displayName={friend.displayName} picture={friend.picture} avatar={friend.avatar} size="sm" online={online} />;
}

function ActiveSubtitle({ friend }: { friend: Friend }) {
  const online = useHub((s) => s.online.has(friend.id));
  return <span className="subtitle">{online ? 'Online now' : 'Offline — they will see this later'}</span>;
}

// ── friend list ────────────────────────────────────────────────────────────

function FriendList({ activeId, onPick }: { activeId: string | null; onPick: (id: string) => void }) {
  const friends = useHub((s) => s.friends);
  const online = useHub((s) => s.online);
  const unread = useHub((s) => s.unread);
  const conversations = useHub((s) => s.conversations);
  const close = useHub((s) => s.closeChat);
  const navigate = useNavigate();

  const sorted = useMemo(() => {
    const lastAt = (id: string) => {
      const list = conversations[id];
      return list && list.length ? list[list.length - 1]?.createdAt ?? '' : '';
    };
    return [...friends].sort((a, b) => {
      const ua = unread[a.id] ?? 0;
      const ub = unread[b.id] ?? 0;
      if (ua !== ub) return ub - ua;
      const la = lastAt(a.id);
      const lb = lastAt(b.id);
      if (la !== lb) return lb.localeCompare(la);
      const oa = online.has(a.id) ? 1 : 0;
      const ob = online.has(b.id) ? 1 : 0;
      if (oa !== ob) return ob - oa;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [friends, unread, conversations, online]);

  if (friends.length === 0) {
    return (
      <div className="chat-list">
        <div className="empty">
          <div className="empty-emoji" aria-hidden="true">
            👯
          </div>
          <h4>No friends yet</h4>
          <p>Add a friend to start chatting.</p>
          <Button
            size="sm"
            variant="gold"
            onClick={() => {
              close();
              navigate('/friends');
            }}
          >
            Add a friend
          </Button>
        </div>
      </div>
    );
  }

  return (
    <nav className="chat-list" aria-label="Conversations">
      {sorted.map((f) => {
        const list = conversations[f.id];
        const last = list && list.length ? list[list.length - 1] : undefined;
        const n = unread[f.id] ?? 0;
        return (
          <button key={f.id} type="button" className={cx('chat-friend', f.id === activeId && 'active', n > 0 && 'unread')} onClick={() => onPick(f.id)} aria-current={f.id === activeId ? 'true' : undefined}>
            <UserAvatar displayName={f.displayName} picture={f.picture} avatar={f.avatar} size="sm" online={online.has(f.id)} />
            <span style={{ minWidth: 0 }}>
              <span className="chat-friend-name">{f.displayName}</span>
              <br />
              <span className="chat-friend-preview">{last ? last.body : online.has(f.id) ? 'Online' : 'Say hi 👋'}</span>
            </span>
            {n > 0 ? <span className="chat-unread">{n > 99 ? '99+' : n}</span> : <span />}
          </button>
        );
      })}
    </nav>
  );
}

function NoThread({ hasFriends }: { hasFriends: boolean }) {
  return (
    <div className="chat-empty-pane">
      <div className="empty">
        <div className="empty-emoji" aria-hidden="true">
          💬
        </div>
        <h4>{hasFriends ? 'Pick a friend to chat' : 'Nothing here yet'}</h4>
        <p>{hasFriends ? 'Conversations are private between the two of you.' : 'Once you have friends, your chats live here.'}</p>
      </div>
    </div>
  );
}

// ── thread ─────────────────────────────────────────────────────────────────

type Row = { kind: 'day'; key: string; label: string } | { kind: 'new'; key: string } | { kind: 'msg'; key: string; msg: DirectMessage; grouped: boolean; showMeta: boolean };

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (same(d, today)) return 'Today';
  if (same(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) });
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function buildRows(messages: DirectMessage[], me: string, firstUnreadId: string | null): Row[] {
  const rows: Row[] = [];
  let lastDay = '';
  let prev: DirectMessage | null = null;
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (!m) continue;
    const day = new Date(m.createdAt).toDateString();
    if (day !== lastDay) {
      rows.push({ kind: 'day', key: `day-${day}`, label: dayLabel(m.createdAt) });
      lastDay = day;
      prev = null;
    }
    if (firstUnreadId && m.id === firstUnreadId && m.senderId !== me) rows.push({ kind: 'new', key: 'new' });
    const grouped = !!prev && prev.senderId === m.senderId && new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < 3 * 60_000;
    const next = messages[i + 1];
    const showMeta = !next || next.senderId !== m.senderId || new Date(next.createdAt).getTime() - new Date(m.createdAt).getTime() >= 3 * 60_000;
    rows.push({ kind: 'msg', key: m.id, msg: m, grouped, showMeta });
    prev = m;
  }
  return rows;
}

function Thread({ friend }: { friend: Friend }) {
  const me = useSession((s) => s.user?.id ?? '');
  const messages = useHub((s) => s.conversations[friend.id]);
  const meta = useHub((s) => s.conversationMeta[friend.id]);
  const loadConversation = useHub((s) => s.loadConversation);
  const markRead = useHub((s) => s.markRead);
  const list = messages ?? EMPTY_MESSAGES;

  // Remember where "new" started when this thread was opened so the marker
  // does not jump around as reads are recorded.
  const [firstUnreadId, setFirstUnreadId] = useState<string | null>(null);
  useEffect(() => {
    const first = list.find((m) => m.senderId !== me && !m.readAt);
    setFirstUnreadId(first?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friend.id, meta?.loaded]);

  useEffect(() => {
    void loadConversation(friend.id);
  }, [friend.id, loadConversation]);

  // Mark read when new messages arrive while we are looking.
  useEffect(() => {
    if (list.some((m) => m.senderId !== me && !m.readAt)) void markRead(friend.id);
  }, [list.length, friend.id, me, markRead, list]);

  const rows = useMemo(() => buildRows(list, me, firstUnreadId), [list, me, firstUnreadId]);

  // Auto-scroll: stick to the bottom unless the reader scrolled up.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(true);
  const [pendingBelow, setPendingBelow] = useState(false);
  const lastCount = useRef(0);
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    setStuck(atBottom);
    if (atBottom) setPendingBelow(false);
    if (el.scrollTop < 40 && meta?.hasMore && !meta.loading) {
      const oldest = list[0];
      if (oldest) {
        const prevHeight = el.scrollHeight;
        void loadConversation(friend.id, { before: oldest.createdAt }).then(() => {
          requestAnimationFrame(() => {
            if (scrollRef.current) scrollRef.current.scrollTop += scrollRef.current.scrollHeight - prevHeight;
          });
        });
      }
    }
  }, [meta?.hasMore, meta?.loading, list, loadConversation, friend.id]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const grew = list.length > lastCount.current;
    const lastMine = list[list.length - 1]?.senderId === me;
    if (stuck || lastMine || lastCount.current === 0) {
      el.scrollTop = el.scrollHeight;
      setPendingBelow(false);
    } else if (grew) {
      setPendingBelow(true);
    }
    lastCount.current = list.length;
  }, [list, me, stuck]);

  useEffect(() => {
    lastCount.current = 0;
  }, [friend.id]);

  return (
    <>
      <div className="chat-scroll" ref={scrollRef} onScroll={onScroll} role="log" aria-live="polite" aria-label={`Conversation with ${friend.displayName}`}>
        {meta?.loading && list.length === 0 ? (
          <div className="empty">
            <Spinner />
          </div>
        ) : null}
        {meta?.error ? (
          <div className="empty">
            <p className="help help-error">{meta.error}</p>
            <Button size="sm" variant="ghost" onClick={() => void loadConversation(friend.id)}>
              Try again
            </Button>
          </div>
        ) : null}
        {!meta?.loading && !meta?.error && list.length === 0 ? (
          <div className="empty" style={{ marginTop: 'auto', marginBottom: 'auto' }}>
            <UserAvatar displayName={friend.displayName} picture={friend.picture} avatar={friend.avatar} size="lg" />
            <h4>Say hi to {friend.displayName}</h4>
            <p>This is the start of your conversation.</p>
          </div>
        ) : null}
        {meta?.loading && list.length > 0 ? (
          <div style={{ alignSelf: 'center' }}>
            <Spinner size={16} />
          </div>
        ) : null}
        {rows.map((r) => {
          if (r.kind === 'day') {
            return (
              <div key={r.key} className="chat-day">
                {r.label}
              </div>
            );
          }
          if (r.kind === 'new') {
            return (
              <div key={r.key} className="chat-new-divider">
                New
              </div>
            );
          }
          const mine = r.msg.senderId === me;
          const pending = r.msg.id.startsWith('tmp-');
          const emojiOnly = EMOJI_ONLY.test(r.msg.body.trim());
          return (
            <div key={r.key} className={cx('msg', mine ? 'msg-mine' : 'msg-theirs', r.grouped && 'msg-grouped', pending && 'msg-pending', emojiOnly && 'msg-emoji')}>
              <div className="msg-bubble">{r.msg.body}</div>
              {r.showMeta ? (
                <div className="msg-meta">
                  {pending ? 'Sending…' : timeLabel(r.msg.createdAt)}
                  {mine && !pending && r.msg.readAt ? ' · Seen' : ''}
                </div>
              ) : null}
            </div>
          );
        })}
        {pendingBelow ? (
          <Button
            size="sm"
            variant="gold"
            className="chat-jump"
            onClick={() => {
              const el = scrollRef.current;
              if (el) el.scrollTop = el.scrollHeight;
              setPendingBelow(false);
              setStuck(true);
            }}
          >
            New messages ↓
          </Button>
        ) : null}
      </div>
      <Composer friend={friend} />
    </>
  );
}

// ── composer ───────────────────────────────────────────────────────────────

function Composer({ friend }: { friend: Friend }) {
  const sendDm = useHub((s) => s.sendDm);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setText('');
    // Focus on desktop only; on phones the keyboard popping up is annoying.
    if (window.matchMedia('(min-width: 721px)').matches) ref.current?.focus();
  }, [friend.id]);

  // Grow with content up to the CSS max-height.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(140, el.scrollHeight)}px`;
  }, [text]);

  const remaining = DM_MAX_LENGTH - text.length;
  const canSend = text.trim().length > 0 && remaining >= 0 && !sending;

  const send = async () => {
    if (!canSend) return;
    const body = text;
    setText('');
    setSending(true);
    try {
      await sendDm(friend.id, body);
    } catch (err) {
      setText(body);
      toast.error(err instanceof Error ? err.message : 'Message not sent');
    } finally {
      setSending(false);
      ref.current?.focus();
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  };

  const addEmoji = (emoji: string) => {
    const el = ref.current;
    if (!el) {
      setText((t) => t + emoji);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = text.slice(0, start) + emoji + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + emoji.length, start + emoji.length);
    });
  };

  return (
    <form
      className="chat-compose"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <div className="chat-compose-row">
        <textarea
          ref={ref}
          className="chat-textarea"
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={`Message ${friend.displayName}…`}
          aria-label={`Message ${friend.displayName}`}
          maxLength={DM_MAX_LENGTH + 50}
          enterKeyHint="send"
        />
        <Button type="submit" className="chat-send" variant="primary" disabled={!canSend} aria-label="Send" title="Send (Enter)">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 12 20 4l-4 16-4-7-8-1Z" />
          </svg>
        </Button>
      </div>
      <div className="chat-compose-foot">
        <div className="emoji-row" aria-label="Quick emoji">
          {QUICK_EMOJI.map((e) => (
            <button key={e} type="button" className="emoji-btn" onClick={() => addEmoji(e)} aria-label={`Add ${e}`}>
              {e}
            </button>
          ))}
        </div>
        <span className={cx('char-count', remaining < 50 && 'warn', remaining < 0 && 'over')} aria-live={remaining < 50 ? 'polite' : 'off'}>
          {remaining < 100 ? `${remaining}` : ''}
          <span className="sr-only"> characters left</span>
        </span>
      </div>
    </form>
  );
}
